import { useClientFeature } from "@/hooks/useClientFeature";
import { useTapToPayDevice } from "@/pages/Dashboard/Integrations/PaymentTerminals/useTapToPayDevice";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { useState } from "react";

const SEEN_KEY = "tap-to-pay-awareness-seen";

function hasSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) !== null;
  } catch {
    return true;
  }
}

/**
 * Apple requires telling every eligible user once that Tap to Pay on iPhone
 * exists. Eligible means this iPhone can take it and the user may enable it.
 * Kept per device: the announcement is about this iPhone.
 */
export function useTapToPayAwareness() {
  const { availability, needsActivation, productName } = useTapToPayDevice();
  const { isFeatureEnabled } = useClientFeature();
  const canManage: boolean = useUserPermissionsStore(
    (state: { permissions: Record<string, boolean | undefined> }) =>
      state.permissions.view_integrations_page === true,
  );
  const [isSeen, setIsSeen] = useState(hasSeen);

  const shouldShow =
    !isSeen &&
    needsActivation &&
    availability?.available === true &&
    isFeatureEnabled("paymentTerminals") &&
    canManage;

  function markSeen() {
    try {
      localStorage.setItem(SEEN_KEY, new Date().toISOString());
    } catch {
      // Worst case the announcement shows again next launch.
    }
    setIsSeen(true);
  }

  return { shouldShow, productName: productName ?? "Tap to Pay", markSeen };
}
